import {
  Alert,
  BankIcon,
  BuildingIcon,
  CoinsStackIcon,
  FeaturedIcons,
  FiFileText,
  FiUsers,
  useTheme,
} from '@agrotoken-tech/ui';

import { useTranslation } from '@bcba/react-hooks';
import { ExplorerViewCompanyDigitalDossierSerializer } from '@bcba/serializers';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo } from 'react';
import { useUserContext } from '../../contexts/UserContext';
import {
  AsideTabData,
  DigitalDossierAside,
} from '../DigitalDossier/shared/DigitalDossierAside';
import { ReadOnlyBankingInfoSection } from './components/ReadOnlyBankingInfoSection';
import { ReadOnlyDocsAndFormsInfoSection } from './components/ReadOnlyDocsAndFormsInfoSection';
import { ReadOnlyGeneralInfoSection } from './components/ReadOnlyGeneralInfoSection';
import { ReadOnlyRepresentantsInfoSection } from './components/ReadOnlyRepresentantsInfoSection';
import { ReadOnlyTaxInfoSection } from './components/ReadOnlyTaxInfoSection';

type Props = {
  data: ExplorerViewCompanyDigitalDossierSerializer;
};

export const SharedDigitalDossierSection = ({ data }: Props) => {
  const { t } = useTranslation();
  const { userHasPendingChanges, setUserHasPendingChanges } = useUserContext();

  const {
    generalInformation: { name },
    dossierStatus: { rucaNotCurrentWarning },
  } = data;

  const {
    explorerDigitalDossierEnableTaxInfo,
    explorerDigitalDossierEnableRepresentantsInfo,
    explorerDigitalDossierEnableFormsAndDocsInfo,
  } = useFlags();

  const tabsData = useMemo(() => {
    const tabs: AsideTabData[] = [
      {
        title: t('common.companyGeneralInformation'),
        content: <ReadOnlyGeneralInfoSection data={data.generalInformation} />,
        icon: <FeaturedIcons icon={BuildingIcon} variant="primary" size="sm" />,
      },
      {
        title: t('common.bankData'),
        content: (
          <ReadOnlyBankingInfoSection
            data={data.bankingInformation}
            companyName={name}
          />
        ),
        icon: <FeaturedIcons icon={BankIcon} variant="primary" size="sm" />,
      },
    ];
    if (explorerDigitalDossierEnableTaxInfo) {
      tabs.push({
        title: t('common.taxData'),
        content: <ReadOnlyTaxInfoSection data={data.taxInformation} />,
        icon: (
          <FeaturedIcons icon={CoinsStackIcon} variant="primary" size="sm" />
        ),
      });
    }
    if (explorerDigitalDossierEnableRepresentantsInfo) {
      tabs.push({
        title: t('common.legalRepresentatives'),
        content: (
          <ReadOnlyRepresentantsInfoSection
            data={data.representantInformation}
          />
        ),
        icon: <FeaturedIcons icon={FiUsers} variant="primary" size="sm" />,
      });
    }
    if (explorerDigitalDossierEnableFormsAndDocsInfo) {
      tabs.push({
        title: t('common.documentationAndForms'),
        content: (
          <ReadOnlyDocsAndFormsInfoSection
            data={data.formsAndDocsInformation}
          />
        ),
        icon: <FeaturedIcons icon={FiFileText} variant="primary" size="sm" />,
      });
    }
    return tabs;
  }, [
    t,
    data,
    explorerDigitalDossierEnableTaxInfo,
    explorerDigitalDossierEnableRepresentantsInfo,
    explorerDigitalDossierEnableFormsAndDocsInfo,
  ]);

  const theme = useTheme();

  return (
    <DigitalDossierAside
      tabsData={tabsData}
      theme={theme}
      alertsSpot={
        rucaNotCurrentWarning ? (
          <Alert
            title={t(
              `explorer.digitalDossier.rucaNotCurrentWarning.${rucaNotCurrentWarning}.title`,
            )}
            description={t(
              `explorer.digitalDossier.rucaNotCurrentWarning.${rucaNotCurrentWarning}.text`,
            )}
            variant="error"
            showCloseButton={false}
          />
        ) : null
      }
      hasUserPendingChanges={userHasPendingChanges}
      setUserHasPendingChanges={setUserHasPendingChanges}
    />
  );
};
